const ANALYSIS_INPUT_EXTENSIONS = [".csv", ".xls", ".xlsx"] as const;

const SHARED_FILES_EXTENSIONS = [
  ".pdf",
  ".docx",
  ".txt",
  ".md",
  ".csv",
  ".xls",
  ".xlsx",
  ".pptx",
  ".mp4",
  ".png",
  ".jpg",
  ".jpeg",
  ".zip",
  ".gz",
  ".json",
  ".xml",
  ".yaml",
  ".yml",
] as const;

/**
 * What a folder accepts. IAM enforces the file types (see infra/components/auth.py);
 * the size limit is a client-side guardrail only.
 */
export type UploadRules = {
  readonly allowedExtensions: readonly string[];
  readonly description: string;
  readonly maxSizeBytes: number;
};

/** Analysis inputs: the LiverPRO formats only. */
export const ANALYSIS_INPUT_RULES: UploadRules = {
  allowedExtensions: ANALYSIS_INPUT_EXTENSIONS,
  description: "CSV and Excel (.csv, .xls, .xlsx) files",
  maxSizeBytes: 100 * 1000 * 1000,
};

/** Files Evido shares with a project: reports, guides, images and bundles. */
export const SHARED_FILES_RULES: UploadRules = {
  allowedExtensions: SHARED_FILES_EXTENSIONS,
  description: "documents, images, media and archives",
  maxSizeBytes: 1000 * 1000 * 1000,
};

/** The `accept` attribute for a file dialogue; browsers match it case-insensitively. */
export function uploadAcceptAttribute(rules: UploadRules): string {
  return rules.allowedExtensions.join(",");
}

function fileExtension(fileName: string): string {
  const lastDotIndex = fileName.lastIndexOf(".");
  if (lastDotIndex <= 0) {
    return "";
  }
  return fileName.slice(lastDotIndex);
}

export type ExtensionVerdict = "allowed" | "wrongCase" | "unsupported";

/**
 * Why a file's type is or is not acceptable.
 */
export function extensionVerdict(
  fileName: string,
  allowedExtensions: readonly string[],
): ExtensionVerdict {
  const extension = fileExtension(fileName);
  const matched = allowedExtensions.find(
    (allowed) => allowed.toLowerCase() === extension.toLowerCase(),
  );
  if (extension === "" || !matched) {
    return "unsupported";
  }
  return extension === matched.toLowerCase() ||
    extension === matched.toUpperCase()
    ? "allowed"
    : "wrongCase";
}

export function hasAllowedExtension(
  fileName: string,
  allowedExtensions: readonly string[],
): boolean {
  return extensionVerdict(fileName, allowedExtensions) === "allowed";
}

export function isWithinSizeLimit(
  sizeInBytes: number,
  maxSizeBytes: number,
): boolean {
  return sizeInBytes <= maxSizeBytes;
}

export function isIndividualFile(file: File): boolean {
  return !file.webkitRelativePath.includes("/");
}

export function isAcceptableUploadFile(
  file: File,
  rules: UploadRules,
): boolean {
  return (
    isIndividualFile(file) &&
    isWithinSizeLimit(file.size, rules.maxSizeBytes) &&
    hasAllowedExtension(file.name, rules.allowedExtensions)
  );
}

export type UploadPolicy = {
  readonly validateFile: (file: File) => boolean;
  /** The rules in force, for the messages that have to describe them. */
  readonly currentRules: () => UploadRules;
  readonly setRules: (rules: UploadRules) => void;
};

export function createUploadPolicy(): UploadPolicy {
  let rules = ANALYSIS_INPUT_RULES;
  return {
    validateFile: (file) => isAcceptableUploadFile(file, rules),
    currentRules: () => rules,
    setRules: (next) => {
      rules = next;
    },
  };
}
